import { useNavigate, Link } from 'react-router-dom'
import { AddButton, ConfirmModal, PageHeader } from 'components'
import SAGrid, { GridGetDataProps, GetDataResponse } from 'components/Grid/SAGrid'
import { Col, Row, Button } from 'react-bootstrap'
import { DcsPermissions, ICustomColumn, IPageProps } from 'types'
import { GridCellProps, GridRowClickEvent } from '@progress/kendo-react-grid'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck, faTimes } from '@fortawesome/free-solid-svg-icons'
import { useState } from 'react'
import { V1ApiIntegrationsClient } from 'core/http-common'
import { AudioEyeSiteDto } from 'generated/v1/v1IntegrationsApi'
import toast from 'core/toast'
import { getErrors } from 'core/helpers'
import { hasPermission } from 'state/reduxHelpers'
import { DEFAULT_PERMISSION_MESSAGE } from 'core/consts'
import { anyPermissionsRedirect } from 'core/helpers'

const AudioEyePage = ({ title }: IPageProps) => {

    const navigate = useNavigate()
    const [refreshKey, setRefreshKey] = useState(0)
    const [siteToChangeStatus, setSiteToChangeStatus] = useState<AudioEyeSiteDto | undefined>(undefined)
    const [statusChangeAction, setStatusChangeAction] = useState<'cancel' | 'reactivate' | undefined>(undefined)
    const [statusChangeLoading, setStatusChangeLoading] = useState(false)
    const [statusChangeErrors, setStatusChangeErrors] = useState<string[]>([])

    const canEdit = hasPermission(DcsPermissions.EditAudioEye)
    const canDelete = hasPermission(DcsPermissions.DeleteAudioEye)

    const DealerIdCell = (props: GridCellProps) => (
        <td style={{ textAlign: 'center' }}>
            <Link to={`/dealers/edit/${props.dataItem.dealerId}`}>{props.dataItem.dealerId}</Link>
        </td>
    )

    const StatusCell = (props: GridCellProps) => (
        <td style={{ textAlign: 'center' }}>
            {props.dataItem.status === 'active'
                ? <FontAwesomeIcon icon={faCheck} className='text-success' />
                : <FontAwesomeIcon icon={faTimes} className='text-danger' />}
        </td>
    )

    const openStatusChangeModal = (item: AudioEyeSiteDto, action: 'cancel' | 'reactivate') => {
        const requiredPermission = action === 'cancel' ? canDelete : canEdit
        if (!requiredPermission) {
            toast.error(DEFAULT_PERMISSION_MESSAGE)
            return
        }

        setStatusChangeErrors([])
        setSiteToChangeStatus(item)
        setStatusChangeAction(action)
    }

    const ActionCell = (props: GridCellProps) => {
        const item = props.dataItem as AudioEyeSiteDto
        const isActive = item.status === 'active'

        return (
            <td style={{ textAlign: 'center' }}>
                <Button
                    variant={isActive ? 'danger' : 'success'}
                    size='sm'
                    onClick={(e) => {
                        e.stopPropagation()
                        openStatusChangeModal(item, isActive ? 'cancel' : 'reactivate')
                    }}
                >
                    {isActive ? 'Cancel' : 'Reactivate'}
                </Button>
            </td>
        )
    }

    const columns: ICustomColumn[] = [
        { field: 'dealerId', title: 'DID', width: '80px', cell: DealerIdCell, headerClassName: 'text-center' },
        { field: 'dealerName', title: 'Dealer Name' },
        { field: 'websiteUrl', title: 'Domain' },
        { field: 'status', title: 'Status', width: '100px', cell: StatusCell, headerClassName: 'text-center' },
        { field: 'location', title: 'Location' },
        { field: 'color', title: 'Color', width: '120px' },
        { field: 'toolbarTheme', title: 'Toolbar Theme', width: '150px' },
        { field: 'action', title: 'Action', width: '120px', cell: ActionCell, headerClassName: 'text-center' },
    ]

    const rowClicked = (e: GridRowClickEvent) => {
        if (!canEdit) {
            toast.error(DEFAULT_PERMISSION_MESSAGE)
            return
        }

        navigate(`/integrations/audioeye/edit/${e.dataItem.id}`)
    }

    const fetch = async (props: GridGetDataProps): Promise<GetDataResponse> => {
        try {
            const result = await V1ApiIntegrationsClient.getSites(
                props.search,
                props.sortBy,
                props.sortAscending,
                props.paging && props.paging.currentPage ? props.paging.currentPage : 1,
                props.paging ? props.paging.take : 50,
                true
            )

            console.log(result)
            return {
                data: result.data,
                total: result.totalItems ? result.totalItems : 0
            }
        }
        catch (e) {
            toast.responseErrors(e)
            return {
                data: [],
                total: 0
            }
        }
    }

    const handleStatusChangeConfirmed = async () => {
        if (!siteToChangeStatus?.id || !statusChangeAction) return

        try {
            setStatusChangeLoading(true)
            setStatusChangeErrors([])

            if (statusChangeAction === 'cancel')
                await V1ApiIntegrationsClient.cancelSite(siteToChangeStatus.id)
            else
                await V1ApiIntegrationsClient.reactivateSite(siteToChangeStatus.id)

            toast.success(statusChangeAction === 'cancel'
                ? 'AudioEye site cancelled successfully'
                : 'AudioEye site reactivated successfully')

            setSiteToChangeStatus(undefined)
            setStatusChangeAction(undefined)
            setRefreshKey(refreshKey + 1)
        } catch (e) {
            setStatusChangeErrors(getErrors(e))
        } finally {
            setStatusChangeLoading(false)
        }
    }

    return (
        <div id='AudioEyePage'>
            <PageHeader title={title}>
                <AddButton
                    className='btn-lg btn-primary btnWidth mb-2 mb-md-0'
                    onClick={() => anyPermissionsRedirect([DcsPermissions.AddAudioEye], '/integrations/audioeye/add', navigate)}>
                    Add Audio Eye To Dealer
                </AddButton>
            </PageHeader>

            <Row>
                <Col>
                    <SAGrid
                        key={refreshKey}
                        columns={columns}
                        getData={fetch}
                        defaultSort={[{ field: 'dealerName', dir: 'asc' }]}
                        features={{
                            pageable: true,
                            sortable: true,
                            reorderColumns: true,
                            simpleSearch: true,
                        }}
                        onRowClick={rowClicked}
                    />
                </Col>
            </Row>

            <ConfirmModal
                show={!!siteToChangeStatus}
                loading={statusChangeLoading}
                disabled={statusChangeLoading}
                errors={statusChangeErrors}
                title={statusChangeAction === 'cancel' ? 'Cancel AudioEye Site' : 'Reactivate AudioEye Site'}
                confirmButtonTitle={statusChangeAction === 'cancel' ? 'Cancel Site' : 'Reactivate'}
                confirmButtonVariant={statusChangeAction === 'cancel' ? 'danger' : 'success'}
                content={statusChangeAction === 'cancel'
                    ? `Are you sure you want to cancel the AudioEye integration for ${siteToChangeStatus?.dealerName}? This will remove the AudioEye script and open a removal case.`
                    : `Are you sure you want to reactivate the AudioEye integration for ${siteToChangeStatus?.dealerName}? This requires the dealer to still have Premium ADA, and will open an install case.`}
                onCancel={() => {
                    setSiteToChangeStatus(undefined)
                    setStatusChangeAction(undefined)
                }}
                onSave={handleStatusChangeConfirmed}
            />
        </div>
    )
}

export default AudioEyePage
